import { useState, useRef, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { 
  Camera, Upload, FileText, Loader2, ArrowLeft, Sparkles,
  History, X, CheckCircle2, AlertTriangle, RotateCcw, Maximize2
} from "lucide-react";
import { API } from "@/constants";

const DocumentScannerPage = () => {
  const [mode, setMode] = useState("upload"); // upload | camera | analyzing | result
  const [analysisType, setAnalysisType] = useState("general");
  const [context, setContext] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const ANALYSIS_TYPES = [
    { value: "general", label: "General Analysis", desc: "Comprehensive document review" },
    { value: "contract", label: "Contract Review", desc: "Terms, obligations, risks" },
    { value: "medical", label: "Medical Records", desc: "Patient info, billing, treatment" },
    { value: "evidence", label: "Evidence Review", desc: "Authenticity, evidentiary value" },
    { value: "billing", label: "Billing Analysis", desc: "Charges, irregularities" },
    { value: "pleading", label: "Pleading Review", desc: "Claims, defenses, arguments" },
    { value: "insurance", label: "Insurance Analysis", desc: "Coverage, exclusions, bad faith" },
  ];

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/scanner/history?limit=10`, { withCredentials: true });
      setHistory(res.data.scans || []);
    } catch (e) { /* ignore */ }
  };

  const handleFileSelect = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const url = URL.createObjectURL(file);
    setPreview(url);
    setError("");
    await analyzeFile(file);
  }, [analysisType, context]);

  const handleCameraCapture = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const url = URL.createObjectURL(file);
    setPreview(url);
    setError("");
    await analyzeFile(file);
  }, [analysisType, context]);

  const analyzeFile = async (file) => {
    setLoading(true);
    setMode("analyzing");
    setResult(null);
    setError("");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("analysis_type", analysisType);
    formData.append("context", context);

    try {
      const res = await axios.post(`${API}/scanner/analyze`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setResult(res.data);
      setMode("result");
      fetchHistory();
    } catch (e) {
      setError(e.response?.data?.detail || "Analysis failed. Try again.");
      setMode("upload");
    } finally {
      setLoading(false);
    }
  };

  const resetScanner = () => {
    setMode("upload");
    setResult(null);
    setPreview(null);
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="document-scanner">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-900 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <Link to="/dashboard" className="text-slate-400 hover:text-white text-sm flex items-center gap-1 mb-2">
                <ArrowLeft className="h-3 w-3" /> Dashboard
              </Link>
              <div className="flex items-center gap-3">
                <Camera className="h-6 w-6 text-indigo-400" />
                <h1 className="text-xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Document Scanner</h1>
              </div>
              <p className="text-sm text-slate-400 mt-1">Capture or upload documents for instant AI analysis</p>
            </div>
            <Button 
              variant="outline" 
              className="border-slate-600 text-slate-200 hover:bg-slate-800"
              onClick={() => setShowHistory(!showHistory)}
              data-testid="toggle-history-btn"
            >
              <History className="h-4 w-4 mr-1.5" /> History
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-6 space-y-6">
        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="py-3 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <p className="text-sm text-red-700">{error}</p>
              <Button size="sm" variant="ghost" onClick={() => setError("")} className="ml-auto"><X className="h-3 w-3" /></Button>
            </CardContent>
          </Card>
        )}

        {/* Scan History */}
        {showHistory && history.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <History className="h-4 w-4 text-slate-500" /> Recent Scans
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {history.map((scan, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer"
                    onClick={() => { setResult(scan); setMode("result"); }}
                    data-testid={`history-item-${i}`}
                  >
                    <div>
                      <p className="text-sm font-medium">{scan.filename || "Document"}</p>
                      <p className="text-xs text-slate-400">{scan.analysis_type} - {scan.created_at?.split("T")[0]}</p>
                    </div>
                    <Badge variant="outline" className="text-xs capitalize">{scan.analysis_type}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upload / Camera Mode */}
        {(mode === "upload" || mode === "analyzing") && (
          <>
            {/* Analysis Type & Context */}
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Analysis Type</CardTitle>
                </CardHeader>
                <CardContent>
                  <Select value={analysisType} onValueChange={setAnalysisType} data-testid="analysis-type-select">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ANALYSIS_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>
                          <div className="flex flex-col">
                            <span>{t.label}</span>
                            <span className="text-xs text-slate-400">{t.desc}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Context (Optional)</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    placeholder="Add context about the document or specific questions..."
                    value={context}
                    onChange={(e) => setContext(e.target.value)}
                    rows={2}
                    className="text-sm"
                    data-testid="context-input"
                  />
                </CardContent>
              </Card>
            </div>

            {/* Capture Area */}
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-20 bg-slate-50">
                    <Loader2 className="h-10 w-10 animate-spin text-indigo-600 mb-4" />
                    <p className="text-sm font-medium">Analyzing document...</p>
                    <p className="text-xs text-slate-400 mt-1">AI is reviewing your document</p>
                  </div>
                ) : preview ? (
                  <div className="relative">
                    <img src={preview} alt="Preview" className="w-full max-h-96 object-contain bg-slate-100" />
                    <Button size="sm" variant="secondary" className="absolute top-3 right-3" onClick={resetScanner}>
                      <X className="h-3 w-3 mr-1" /> Clear
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 bg-gradient-to-b from-slate-50 to-white">
                    <div className="w-20 h-20 rounded-2xl bg-indigo-50 flex items-center justify-center mb-4">
                      <Camera className="h-10 w-10 text-indigo-400" />
                    </div>
                    <h3 className="text-lg font-semibold mb-1">Scan a Document</h3>
                    <p className="text-sm text-slate-400 mb-6">Take a photo or upload an image of a legal document</p>
                    
                    <div className="flex gap-3">
                      {/* Camera Button - works on mobile */}
                      <Button 
                        onClick={() => cameraInputRef.current?.click()}
                        className="bg-indigo-600 hover:bg-indigo-500"
                        data-testid="camera-capture-btn"
                      >
                        <Camera className="h-4 w-4 mr-2" /> Take Photo
                      </Button>
                      
                      {/* Upload Button */}
                      <Button 
                        variant="outline" 
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="file-upload-btn"
                      >
                        <Upload className="h-4 w-4 mr-2" /> Upload File
                      </Button>
                    </div>
                    
                    <p className="text-[10px] text-slate-400 mt-4">Supports JPEG, PNG, WEBP up to 20MB</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Result Mode */}
        {mode === "result" && result && (
          <>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <h2 className="text-lg font-semibold">Analysis Complete</h2>
              </div>
              <Button variant="outline" onClick={resetScanner} data-testid="scan-another-btn">
                <RotateCcw className="h-4 w-4 mr-1.5" /> Scan Another
              </Button>
            </div>

            {/* Document Info */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="h-4 w-4 text-blue-600" /> {result.filename || "Document"}
                  </CardTitle>
                  <Badge className="bg-indigo-100 text-indigo-800 text-xs capitalize">{result.analysis_type}</Badge>
                </div>
                <CardDescription className="text-xs">{result.created_at?.split("T")[0]}</CardDescription>
              </CardHeader>
            </Card>

            {/* AI Analysis */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-purple-600" /> AI Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm max-w-none" data-testid="analysis-result">
                  {result.analysis?.split('\n').map((line, i) => {
                    if (!line.trim()) return <br key={i} />;
                    if (line.startsWith('##') || line.startsWith('**')) {
                      return <h3 key={i} className="text-sm font-bold mt-3 mb-1">{line.replace(/[#*]/g, '').trim()}</h3>;
                    }
                    if (line.startsWith('- ') || line.startsWith('* ')) {
                      return <p key={i} className="text-sm text-slate-700 pl-4 py-0.5 border-l-2 border-slate-200">{line.slice(2)}</p>;
                    }
                    return <p key={i} className="text-sm text-slate-700 mb-2">{line}</p>;
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Preview if available */}
            {preview && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Scanned Document</CardTitle>
                </CardHeader>
                <CardContent>
                  <img src={preview} alt="Scanned" className="w-full max-h-80 object-contain rounded-lg bg-slate-50" />
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Hidden inputs */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={handleFileSelect}
          data-testid="file-input"
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          className="hidden"
          onChange={handleCameraCapture}
          data-testid="camera-input"
        />
      </div>
    </div>
  );
};

export default DocumentScannerPage;
